import { useEffect, useState } from 'react';
import { formatDateForInput } from '../../../../../utils/Texts';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';

// 🔹 Configuración dinámica de URLs basada en API_BASE
const SERVER_URL = API_BASE.replace(/\/api\/?$/, ''); // Elimina /api al final
const UPLOAD_ENDPOINT = `${API_BASE}/upload/identificacion`;

// Helper para construir la URL completa de la imagen (S3 o Local)
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path; // Ya es S3
  return `${SERVER_URL}${path}`; // Es local antigua
};

const EMPTY = {
  idPersona: 0,
  tipoIdentificacion: '',
  numero: '',
  fechaExpedicion: '',
  fechaVencimiento: '',
  documentoAdjunto: '',
};

const ID_OPTIONS = ['INE', 'Pasaporte', 'Licencia', 'Otro'];

export default function Modal({
  open,
  mode = 'new',
  data = null,
  fkValue = 0,
  onClose,
  onSubmit,
  existingTypes = [],
  inline = false,
}) {
  const { getSystemConfig } = useLogos();
  EMPTY.idPersona = fkValue;
  const [form, setForm] = useState(EMPTY);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previousImagePath, setPreviousImagePath] = useState(null); // Para eliminar la imagen anterior
  const [errors, setErrors] = useState({
    tipoIdentificacion: '',
    numero: '',
    fechaExpedicion: '',
    fechaVencimiento: '',
    documentoAdjunto: '',
  });

  const availableOptions = ID_OPTIONS.filter((opt) => {
    // Permitir la opción actual en modo edición
    if (mode === 'edit' && data?.tipoIdentificacion === opt) return true;
    // Excluir opciones ya existentes
    return !existingTypes.includes(opt);
  });

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'tipoIdentificacion':
        if (!value) error = 'Selecciona un tipo de identificación.';
        break;
      case 'numero':
        if (!value) error = 'Captura el número de identificación.';
        break;
      case 'fechaExpedicion':
        if (!value) error = 'Selecciona la fecha de expedición.';
        break;
      case 'fechaVencimiento':
        if (!value) error = 'Selecciona la fecha de vencimiento.';
        break;
      case 'documentoAdjunto':
        if (!value) error = 'Selecciona una imagen del documento.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const validateAll = () => {
    const okTipo = validateField('tipoIdentificacion', form.tipoIdentificacion);
    const okNumero = validateField('numero', form.numero);
    const okExp = validateField('fechaExpedicion', form.fechaExpedicion);
    const okVenc = validateField('fechaVencimiento', form.fechaVencimiento);
    const okDoc = validateField('documentoAdjunto', form.documentoAdjunto);
    return okTipo && okNumero && okExp && okVenc && okDoc;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    validateField(field, value);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        documentoAdjunto: 'Solo se permiten archivos de imagen.',
      }));
      return;
    }

    // Validar tamaño dinámico
    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        documentoAdjunto: `La imagen no debe superar los ${maxMB}MB.`,
      }));
      return;
    }

    setSelectedFile(file);

    // Crear una URL temporal para la vista previa inmediata
    const blobUrl = URL.createObjectURL(file);

    // Mostrar vista previa mientras se sube
    setForm((prev) => ({
      ...prev,
      documentoAdjuntoPreview: blobUrl,
    }));

    try {
      setIsUploading(true);
      // 1. ELIMINAR IMAGEN ANTERIOR SI EXISTE EN S3
      if (previousImagePath && previousImagePath.startsWith('http')) {
        try {
          // El backend ahora espera ?filePath=...
          await fetch(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`, {
            method: 'DELETE',
          });
          console.log('Imagen anterior eliminada de S3:', previousImagePath);
        } catch (deleteError) {
          console.warn('No se pudo eliminar la imagen anterior de S3:', deleteError);
        }
      }

      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', file);

      // Enviar al servidor
      const response = await fetch(UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al subir el archivo');
      }

      const data = await response.json();

      // Guardar la nueva ruta como la imagen anterior para futuras actualizaciones
      setPreviousImagePath(data.filePath);

      // Guardar la ruta devuelta por el servidor
      setForm((prev) => ({
        ...prev,
        documentoAdjunto: data.filePath,
        documentoAdjuntoPreview: blobUrl, // Mantener blob URL para vista previa
      }));

      validateField('documentoAdjunto', data.filePath);

    } catch (error) {
      console.error('Error al subir el archivo:', error);
      setErrors((prev) => ({
        ...prev,
        documentoAdjunto: error.message || 'Error al subir la imagen al servidor.',
      }));

      // Limpiar la vista previa si hubo error
      setForm((prev) => ({
        ...prev,
        documentoAdjuntoPreview: null,
      }));
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idPersona: data.idPersona ?? fkValue ?? 0,
        tipoIdentificacion: data.tipoIdentificacion ?? '',
        numero: data.numero ?? '',
        fechaExpedicion: formatDateForInput(data.fechaExpedicion),
        fechaVencimiento: formatDateForInput(data.fechaVencimiento),
        documentoAdjunto: data.documentoAdjunto ?? '',
      };
      setForm(filled);

      // Guardar la ruta de la imagen actual para poder eliminarla si se actualiza
      if (filled.documentoAdjunto) {
        setPreviousImagePath(filled.documentoAdjunto);
      }

      validateField('tipoIdentificacion', filled.tipoIdentificacion);
      validateField('numero', filled.numero);
      validateField('fechaExpedicion', filled.fechaExpedicion);
      validateField('fechaVencimiento', filled.fechaVencimiento);
      validateField('documentoAdjunto', filled.documentoAdjunto);
    } else {
      setForm({
        ...EMPTY,
        idPersona: fkValue,
      });
      setPreviousImagePath(null); // Limpiar la imagen anterior en modo nuevo
      setErrors({
        tipoIdentificacion: '',
        numero: '',
        fechaExpedicion: '',
        fechaVencimiento: '',
        documentoAdjunto: '',
      });
    }
  }, [mode, data, fkValue]);

  const isSaveDisabled =
    !form.tipoIdentificacion?.trim() ||
    !form.numero?.trim() ||
    !form.fechaExpedicion ||
    !form.fechaVencimiento ||
    !form.documentoAdjunto?.trim() ||
    !!errors.tipoIdentificacion ||
    !!errors.numero ||
    !!errors.fechaExpedicion ||
    !!errors.fechaVencimiento ||
    !!errors.documentoAdjunto;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '.25rem',
  };

  if (!open) return null;

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva identificación' : 'Editar identificación'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Tipo:</label>
        <br /><br />
        <select
          className={`infoField${errors.tipoIdentificacion ? ' infoField--error' : ''}`}
          value={form.tipoIdentificacion}
          onChange={(e) => handleChange('tipoIdentificacion', e.target.value)}
        >
          <option value="">Seleccione…</option>
          {availableOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        {errors.tipoIdentificacion && (
          <small style={errorStyle}>{errors.tipoIdentificacion}</small>
        )}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Número:</label>
        <br /><br />
        <input
          className={`infoField${errors.numero ? ' infoField--error' : ''}`}
          value={form.numero}
          onChange={(e) => handleChange('numero', e.target.value)}
          maxLength={60}
        />
        {errors.numero && (
          <small style={errorStyle}>{errors.numero}</small>
        )}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Expedición:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaExpedicion ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.fechaExpedicion}
          onChange={(e) => handleChange('fechaExpedicion', e.target.value)}
        />
        {errors.fechaExpedicion && (
          <small style={errorStyle}>{errors.fechaExpedicion}</small>
        )}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Vencimiento:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaVencimiento ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.fechaVencimiento}
          onChange={(e) => handleChange('fechaVencimiento', e.target.value)}
        />
        {errors.fechaVencimiento && (
          <small style={errorStyle}>{errors.fechaVencimiento}</small>
        )}
      </div>

      <div style={{ gridColumn: '1 / -1' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Documento (Imagen):</label>
        <br /><br />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>

          <div
            className={`file-uploader${form.documentoAdjunto ? ' file-uploader--filled' : ''}${errors.documentoAdjunto ? ' file-uploader--error' : ''}${isUploading ? ' file-uploader--uploading' : ''}`}
          >
            {form.documentoAdjunto && (
              <div className="file-uploader__badge">
                <span>✓</span>
                <span>Archivo cargado</span>
              </div>
            )}

            {form.documentoAdjunto && (
              <div className="file-uploader__preview">
                <img
                  src={form.documentoAdjuntoPreview || getImageUrl(form.documentoAdjunto)}
                  alt="Vista previa del documento"
                />
              </div>
            )}

            <label className="file-uploader__label">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                onClick={(e) => { e.target.value = ''; }}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
              <div className="file-uploader__icon">
                {isUploading ? '⏳' : (form.documentoAdjunto ? '🔄' : '☁️')}
              </div>
              <div className="file-uploader__text">
                {isUploading ? 'Subiendo archivo...' : (form.documentoAdjunto ? 'Cambiar imagen' : 'Haz clic para subir imagen')}
              </div>
              <div className="file-uploader__hint">
                JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
              </div>
            </label>
          </div>

          {errors.documentoAdjunto && (
            <small style={errorStyle}>{errors.documentoAdjunto}</small>
          )}
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (!validateAll()) return;
          onSubmit(form, mode);
        }}
        disabled={isSaveDisabled || isUploading}
        style={{
          opacity: (isSaveDisabled || isUploading) ? 0.5 : 1,
          cursor: (isSaveDisabled || isUploading) ? 'not-allowed' : 'pointer',
        }}
      >
        {isUploading ? 'Subiendo...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
